// Frontend/src/components/issues/IssueBits.tsx
//
// The small pieces both issue pages are built from. Shared so a status chip
// cannot drift into meaning one thing in the admin console and another in
// the resident portal.
//
// Everything here is mobile-first: sizes step UP at `sm:`, never down.

import React from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useOverlay } from "@/lib/overlay";
import {
  Building2,
  Check,
  ChevronLeft,
  ChevronRight,
  FileText,
  Home,
  Loader2,
  Maximize2,
  Paperclip,
  Plus,
  Search,
  Send,
  X,
} from "lucide-react";
import {
  BRAND,
  CATEGORY_ICON_CHOICES,
  PRIORITY_ICON,
  PRIORITY_TONE,
  STATUS_ICON,
  STATUS_TONE,
  cardRailStyle,
  categoryIcon,
  chipStyle,
  iconWellStyle,
  priorityIsLoud,
  quietChipStyle,
} from "@/lib/issueStyles";
import type {
  IssuePriority,
  IssueStatus,
  IssueStatusChange,
  IssueTargetUnit,
} from "@/types/issue";
import { formatBytes, readApiError, type IssueLabels } from "@/lib/issueLabels";


/* ------------------------------------------------------------------ */
export function StatusChip({
  status,
  labels,
  size = "md",
}: {
  status: IssueStatus;
  labels: IssueLabels;
  size?: "sm" | "md";
}) {
  const Icon = STATUS_ICON[status];
  return (
    <span
      className={`inline-flex shrink-0 items-center gap-1.5 rounded-full font-semibold ${
        size === "sm" ? "px-2 py-0.5 text-[11px]" : "px-2.5 py-1 text-xs"
      }`}
      style={chipStyle(STATUS_TONE[status])}
    >
      <Icon className={size === "sm" ? "h-3 w-3" : "h-3.5 w-3.5"} aria-hidden />
      {labels.status[status]}
    </span>
  );
}

/**
 * Only URGENT is filled. The rest are a tinted dot and a quiet label, so a
 * genuinely urgent issue is the one thing that catches the eye in a list.
 */
export function PriorityChip({
  priority,
  labels,
}: {
  priority: IssuePriority;
  labels: IssueLabels;
}) {
  const tone = PRIORITY_TONE[priority];
  const Icon = PRIORITY_ICON[priority];

  if (priorityIsLoud(priority)) {
    return (
      <span
        className="inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide"
        style={chipStyle(tone)}
      >
        <Icon className="h-3 w-3" aria-hidden />
        {labels.priority[priority]}
      </span>
    );
  }

  return (
    <span className="inline-flex shrink-0 items-center gap-1.5 text-[11px] font-medium text-gray-600">
      <span
        className="h-1.5 w-1.5 rounded-full"
        style={{ background: tone.rail }}
        aria-hidden
      />
      {labels.priority[priority]}
    </span>
  );
}

/** Unit, counts, dates — facts that must never compete with status. */
export function QuietChip({
  icon: Icon,
  children,
}: {
  icon?: React.ElementType;
  children: React.ReactNode;
}) {
  return (
    <span
      className="inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium"
      style={quietChipStyle()}
    >
      {Icon && <Icon className="h-3 w-3" aria-hidden />}
      {children}
    </span>
  );
}

/** The card's focal point: the category's icon, tinted by status. */
export function CategoryWell({
  icon,
  status,
  size = "md",
}: {
  icon: string | null | undefined;
  status: IssueStatus;
  size?: "sm" | "md";
}) {
  const Icon = categoryIcon(icon);
  return (
    <span
      className={`grid shrink-0 place-items-center rounded-2xl ${
        size === "sm" ? "h-9 w-9" : "h-11 w-11"
      }`}
      style={iconWellStyle(status)}
      aria-hidden
    >
      <Icon className={size === "sm" ? "h-4 w-4" : "h-5 w-5"} />
    </span>
  );
}

/**
 * The card shell: rounded, glass, with the status rail as a rounded LEFT
 * BORDER.
 *
 * Matches the dashboard's Card, and the difference from the previous
 * absolutely-positioned bar is the whole point — a positioned bar runs
 * square into the rounded corner and reads as a hard edge stuck on the
 * side, where a border follows the radius and looks like part of the card.
 */
export function IssueCardShell({
  status,
  settled,
  children,
  className = "",
}: {
  status: IssueStatus;
  settled?: boolean;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={`overflow-hidden rounded-3xl border-l-4 bg-white/80 ring-1 ring-black/5 shadow-sm shadow-black/[0.03] backdrop-blur-xl transition hover:bg-white hover:shadow-md ${
        settled ? "opacity-[0.85] hover:opacity-100" : ""
      } ${className}`}
      style={cardRailStyle(status)}
    >
      {children}
    </div>
  );
}

/* ------------------------------------------------------------------ */
export function StatusTimeline({
  changes,
  labels,
}: {
  changes: IssueStatusChange[];
  labels: IssueLabels;
}) {
  const { t } = useLanguage();
  if (changes.length === 0) return null;

  return (
    <section>
      <SectionHeading>{t("issues.timeline")}</SectionHeading>
      <ol className="mt-3 space-y-0">
        {changes.map((c, i) => {
          const tone = STATUS_TONE[c.to_status];
          const last = i === changes.length - 1;
          return (
            <li key={c.id} className="relative flex gap-3 pb-4 last:pb-0">
              {/* The connector stops at the last entry rather than trailing
                  off into nothing. */}
              {!last && (
                <span
                  className="absolute left-[7px] top-4 bottom-0 w-px bg-gray-200"
                  aria-hidden
                />
              )}
              <span
                className="relative z-10 mt-1 h-3.5 w-3.5 shrink-0 rounded-full ring-2 ring-white"
                style={{ background: tone.rail }}
                aria-hidden
              />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-900">
                  {c.from_status
                    ? t("issues.timelineChanged", {
                        from: labels.status[c.from_status as IssueStatus],
                        to: labels.status[c.to_status],
                      })
                    : t("issues.timelineReported")}
                </p>
                <p className="mt-0.5 text-[11px] text-gray-500">
                  {new Date(c.created_at).toLocaleString()}
                  {c.changed_by_name ? ` · ${c.changed_by_name}` : ""}
                </p>
                {c.note && (
                  <p className="mt-1.5 rounded-lg bg-gray-50 px-2.5 py-1.5 text-xs text-gray-700 ring-1 ring-gray-100">
                    {c.note}
                  </p>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
}

/* ------------------------------------------------------------------ *
 * Attachments
 *
 * Images open in a lightbox rather than a new tab. On a phone a new tab
 * means leaving the app and finding your way back to the issue you were
 * reading; the photo of the leak belongs beside the report, not instead
 * of it. PDFs still open externally — a browser renders those better than
 * anything worth building here.
 * ------------------------------------------------------------------ */
export type AttachmentLike = {
  id: string;
  file: string | null;
  original_filename: string;
  content_type: string;
  size_bytes: number;
};

export function AttachmentGrid({ attachments }: { attachments: AttachmentLike[] }) {
  const { t } = useLanguage();
  const [viewing, setViewing] = React.useState<number | null>(null);

  const images = attachments.filter(
    (a) => a.file && a.content_type.startsWith("image/")
  );

  return (
    <>
      <div className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-4">
        {attachments.map((a) => {
          if (!a.file) return null;
          const isImage = a.content_type.startsWith("image/");
          const imageIndex = images.findIndex((i) => i.id === a.id);

          const inner = isImage ? (
            <img
              src={a.file}
              alt={a.original_filename}
              loading="lazy"
              className="h-full w-full object-cover transition group-hover:scale-[1.04]"
            />
          ) : (
            <span className="flex h-full w-full flex-col items-center justify-center gap-1 bg-gray-50 text-gray-500">
              <FileText className="h-5 w-5" aria-hidden />
              <span className="text-[10px] font-bold">PDF</span>
            </span>
          );

          const shell =
            "group relative aspect-square overflow-hidden rounded-2xl bg-white ring-1 ring-gray-200 transition hover:ring-[#1F3A93]/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1F3A93]";

          return isImage ? (
            <button
              key={a.id}
              type="button"
              onClick={() => setViewing(imageIndex)}
              className={shell}
              title={a.original_filename}
            >
              {inner}
              <span className="absolute inset-x-0 bottom-0 flex items-center justify-between gap-1 bg-gradient-to-t from-black/55 to-transparent px-1.5 pb-1 pt-4 text-[10px] text-white">
                <span className="truncate">{a.original_filename}</span>
                <Maximize2 className="h-3 w-3 shrink-0 opacity-80" aria-hidden />
              </span>
            </button>
          ) : (
            <a
              key={a.id}
              href={a.file}
              target="_blank"
              rel="noreferrer"
              className={shell}
              title={a.original_filename}
            >
              {inner}
              <span className="absolute inset-x-0 bottom-0 truncate bg-white/90 px-1.5 py-1 text-[10px] text-gray-600">
                {a.original_filename}
              </span>
            </a>
          );
        })}
      </div>

      <p className="mt-1.5 text-[11px] text-gray-500">
        {t("issues.attachmentTotal", {
          count: attachments.length,
          size: formatBytes(attachments.reduce((sum, a) => sum + (a.size_bytes || 0), 0)),
        })}
      </p>

      <Lightbox
        images={images}
        index={viewing}
        onIndex={setViewing}
        onClose={() => setViewing(null)}
      />
    </>
  );
}

function Lightbox({
  images,
  index,
  onIndex,
  onClose,
}: {
  images: AttachmentLike[];
  index: number | null;
  onIndex: (i: number) => void;
  onClose: () => void;
}) {
  const { t } = useLanguage();
  const open = index !== null && index >= 0 && index < images.length;
  useOverlay(open, onClose);

  const go = React.useCallback(
    (delta: number) => {
      if (index === null) return;
      onIndex((index + delta + images.length) % images.length);
    },
    [index, images.length, onIndex]
  );

  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") go(1);
      if (e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, go]);

  if (!open || index === null) return null;
  const current = images[index];

  return (
    <div className="fixed inset-0 z-[70] flex flex-col bg-black/92" role="dialog" aria-modal="true">
      <header className="flex items-center gap-3 px-4 py-3 text-white">
        <span className="min-w-0 flex-1 truncate text-sm">{current.original_filename}</span>
        {images.length > 1 && (
          <span className="shrink-0 text-xs tabular-nums text-white/70">
            {index + 1} / {images.length}
          </span>
        )}
        <button
          type="button"
          onClick={onClose}
          aria-label={t("common.close")}
          className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-white/10 text-white transition hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
        >
          <X className="h-4 w-4" />
        </button>
      </header>

      <div className="relative flex min-h-0 flex-1 items-center justify-center px-2 pb-4">
        <img
          src={current.file ?? ""}
          alt={current.original_filename}
          className="max-h-full max-w-full rounded-xl object-contain"
        />

        {images.length > 1 && (
          <>
            <LightboxArrow side="left" onClick={() => go(-1)} label={t("issues.previousImage")} />
            <LightboxArrow side="right" onClick={() => go(1)} label={t("issues.nextImage")} />
          </>
        )}
      </div>

      <footer className="px-4 pb-[max(1rem,env(safe-area-inset-bottom))] text-center">
        <a
          href={current.file ?? "#"}
          target="_blank"
          rel="noreferrer"
          className="text-xs text-white/70 underline-offset-2 hover:text-white hover:underline"
        >
          {t("issues.openOriginal")}
        </a>
      </footer>
    </div>
  );
}

function LightboxArrow({
  side,
  onClick,
  label,
}: {
  side: "left" | "right";
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className={`absolute top-1/2 grid h-11 w-11 -translate-y-1/2 place-items-center rounded-full bg-white/10 text-white transition hover:bg-white/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white ${
        side === "left" ? "left-2" : "right-2"
      }`}
    >
      {side === "left" ? (
        <ChevronLeft className="h-5 w-5" />
      ) : (
        <ChevronRight className="h-5 w-5" />
      )}
    </button>
  );
}

/* ------------------------------------------------------------------ *
 * Facts
 * ------------------------------------------------------------------ */
export function MetaList({
  items,
}: {
  items: { label: string; value: React.ReactNode; icon?: React.ElementType }[];
}) {
  return (
    <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
      {items.map(({ label, value, icon: Icon }) => (
        <div key={label} className="min-w-0">
          <dt className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.08em] text-gray-500">
            {Icon && <Icon className="h-3 w-3" aria-hidden />}
            {label}
          </dt>
          <dd className="mt-0.5 truncate text-sm font-medium text-gray-900">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

export function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <h4 className="text-[11px] font-bold uppercase tracking-[0.08em] text-gray-500">
      {children}
    </h4>
  );
}

/** Skeleton that mirrors the real card, so loading does not reflow the page. */
export function IssueListSkeleton({ rows = 3 }: { rows?: number }) {
  return (
    <div className="space-y-2.5" aria-hidden>
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          className="overflow-hidden rounded-3xl border-l-4 border-gray-200 bg-white/80 p-4 ring-1 ring-black/5"
        >
          <div className="flex animate-pulse items-start gap-3">
            <div className="h-11 w-11 shrink-0 rounded-2xl bg-gray-200" />
            <div className="min-w-0 flex-1 space-y-2">
              <div className="h-3.5 w-2/5 rounded bg-gray-200" />
              <div className="h-3 w-4/5 rounded bg-gray-100" />
              <div className="flex gap-2 pt-1">
                <div className="h-4 w-16 rounded-full bg-gray-100" />
                <div className="h-4 w-14 rounded-full bg-gray-100" />
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

/** Empty and error states, so a blank list is never ambiguous. */
export function EmptyState({
  icon: Icon,
  title,
  hint,
  action,
  tone = "neutral",
}: {
  icon: React.ElementType;
  title: string;
  hint?: string;
  action?: React.ReactNode;
  tone?: "neutral" | "error";
}) {
  const isError = tone === "error";
  return (
    <div
      className={`flex flex-col items-center gap-3 rounded-3xl px-6 py-12 text-center ${
        isError
          ? "bg-red-50 ring-1 ring-red-200"
          : "border border-dashed border-gray-300 bg-white/70 backdrop-blur"
      }`}
    >
      <span
        className={`grid h-11 w-11 place-items-center rounded-full ${
          isError ? "bg-red-100 text-red-600" : "bg-gray-100 text-gray-400"
        }`}
      >
        <Icon className="h-5 w-5" aria-hidden />
      </span>
      <div>
        <p
          className={`text-sm font-semibold ${
            isError ? "text-red-800" : "text-gray-800"
          }`}
        >
          {title}
        </p>
        {hint && (
          <p
            className={`mx-auto mt-1 max-w-xs text-xs ${
              isError ? "text-red-700" : "text-gray-500"
            }`}
          >
            {hint}
          </p>
        )}
      </div>
      {action}
    </div>
  );
}

export function ApiError({ error }: { error: unknown }) {
  const { t } = useLanguage();
  if (!error) return null;
  return (
    <p className="mt-3 flex items-start gap-2 rounded-xl bg-red-50 px-3 py-2 text-sm text-red-800 ring-1 ring-red-200">
      <svg viewBox="0 0 24 24" className="mt-0.5 h-4 w-4 shrink-0" fill="none" aria-hidden>
        <path
          d="M12 8v5m0 3h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
        />
      </svg>
      <span>{readApiError(error, t("common.error"))}</span>
    </p>
  );
}

/* ------------------------------------------------------------------ *
 * Pager
 *
 * Ten rows a page. Shows a window of page numbers on a wide screen and
 * collapses to "3 / 9" on a phone, because eleven tappable digits in a
 * 360px viewport is not a control anyone can hit.
 * ------------------------------------------------------------------ */
export function Pager({
  page,
  pageSize,
  count,
  onChange,
  busy,
}: {
  page: number;
  pageSize: number;
  count: number;
  onChange: (page: number) => void;
  busy?: boolean;
}) {
  const { t } = useLanguage();
  const pages = Math.max(1, Math.ceil(count / pageSize));
  if (pages <= 1) return null;

  const first = (page - 1) * pageSize + 1;
  const last = Math.min(page * pageSize, count);

  // A window of at most five, clamped so it never runs off either end.
  const windowStart = Math.max(1, Math.min(page - 2, pages - 4));
  const windowEnd = Math.min(pages, windowStart + 4);
  const numbers: number[] = [];
  for (let p = windowStart; p <= windowEnd; p++) numbers.push(p);

  const step = (to: number) => () => {
    onChange(to);
    // Paging without this leaves you halfway down the next page.
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <nav
      className="flex flex-wrap items-center justify-between gap-3 rounded-3xl bg-white/70 px-3 py-2.5 ring-1 ring-black/5 backdrop-blur"
      aria-label={t("issues.pagination")}
    >
      <span className="text-[11px] tabular-nums text-gray-500">
        {t("issues.showingRange", { first, last, count })}
      </span>

      <div className="flex items-center gap-1">
        <PagerButton
          onClick={step(page - 1)}
          disabled={page <= 1 || busy}
          label={t("issues.previousPage")}
        >
          <ChevronLeft className="h-4 w-4" />
        </PagerButton>

        {/* Numbers on a wide screen… */}
        <div className="hidden items-center gap-1 sm:flex">
          {windowStart > 1 && (
            <>
              <PageNumber n={1} active={page === 1} onClick={step(1)} busy={busy} />
              {windowStart > 2 && <span className="px-0.5 text-gray-400">…</span>}
            </>
          )}
          {numbers.map((n) => (
            <PageNumber key={n} n={n} active={n === page} onClick={step(n)} busy={busy} />
          ))}
          {windowEnd < pages && (
            <>
              {windowEnd < pages - 1 && <span className="px-0.5 text-gray-400">…</span>}
              <PageNumber n={pages} active={page === pages} onClick={step(pages)} busy={busy} />
            </>
          )}
        </div>

        {/* …and a compact position on a phone. */}
        <span className="px-2 text-[12px] font-medium tabular-nums text-gray-700 sm:hidden">
          {page} / {pages}
        </span>

        <PagerButton
          onClick={step(page + 1)}
          disabled={page >= pages || busy}
          label={t("issues.nextPage")}
        >
          <ChevronRight className="h-4 w-4" />
        </PagerButton>
      </div>
    </nav>
  );
}

function PagerButton({
  onClick,
  disabled,
  label,
  children,
}: {
  onClick: () => void;
  disabled?: boolean;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      className="grid h-9 w-9 place-items-center rounded-full bg-white text-gray-600 ring-1 ring-gray-200 transition hover:bg-gray-50 disabled:pointer-events-none disabled:opacity-35 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1F3A93]"
    >
      {children}
    </button>
  );
}

function PageNumber({
  n,
  active,
  onClick,
  busy,
}: {
  n: number;
  active: boolean;
  onClick: () => void;
  busy?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      aria-current={active ? "page" : undefined}
      className={`h-9 min-w-9 rounded-full px-2.5 text-[13px] font-semibold tabular-nums transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1F3A93] ${
        active
          ? "text-white shadow-sm"
          : "bg-white text-gray-600 ring-1 ring-gray-200 hover:bg-gray-50"
      }`}
      style={active ? { background: BRAND } : undefined}
    >
      {n}
    </button>
  );
}

/* ------------------------------------------------------------------ *
 * Category picker
 *
 * A tappable grid rather than a select: nine-plus options in a dropdown is
 * nine taps and a scroll on a phone, and the icons make the set readable at
 * a glance.
 *
 * `canCreate` turns on the "+ New" tile. Only admins get it — what a
 * building's categories are is the admin's call, not something a resident
 * can add to from a report form.
 * ------------------------------------------------------------------ */
export function CategoryPicker({
  categories,
  value,
  onChange,
  canCreate,
  onCreate,
  creating,
  loading,
}: {
  categories: IssueCategoryRow[];
  value: string | null;
  onChange: (id: string) => void;
  canCreate?: boolean;
  onCreate?: (name: string, icon: string) => void;
  creating?: boolean;
  loading?: boolean;
}) {
  const { t } = useLanguage();
  const [adding, setAdding] = React.useState(false);
  const [name, setName] = React.useState("");
  const [icon, setIcon] = React.useState("layers");

  if (loading) {
    return (
      <div className="grid grid-cols-3 gap-2" aria-hidden>
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="h-[4.5rem] animate-pulse rounded-2xl bg-gray-100" />
        ))}
      </div>
    );
  }

  function submitNew() {
    const trimmed = name.trim();
    if (trimmed.length < 2 || !onCreate) return;
    onCreate(trimmed, icon);
    setName("");
    setIcon("layers");
    setAdding(false);
  }

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
        {categories.map((c) => {
          const Icon = categoryIcon(c.icon);
          const active = value === c.id;
          return (
            <button
              key={c.id}
              type="button"
              onClick={() => onChange(c.id)}
              aria-pressed={active}
              className={`flex flex-col items-center justify-center gap-1.5 rounded-2xl px-1.5 py-3 text-center transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1F3A93] ${
                active
                  ? "bg-[#E7EBF8] text-[#1B3480]"
                  : "bg-white text-gray-600 ring-1 ring-gray-200 hover:bg-gray-50"
              }`}
              style={active ? { boxShadow: `inset 0 0 0 1.5px ${BRAND}` } : undefined}
            >
              <Icon className="h-5 w-5" aria-hidden />
              <span className="line-clamp-2 text-[11px] font-medium leading-tight">
                {c.name}
              </span>
            </button>
          );
        })}

        {canCreate && !adding && (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="flex flex-col items-center justify-center gap-1.5 rounded-2xl border border-dashed border-gray-300 px-1.5 py-3 text-center text-gray-500 transition hover:border-[#1F3A93]/50 hover:text-[#1B3480] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1F3A93]"
          >
            <Plus className="h-5 w-5" aria-hidden />
            <span className="text-[11px] font-medium leading-tight">
              {t("issues.newCategory")}
            </span>
          </button>
        )}
      </div>

      {canCreate && adding && (
        <div className="space-y-2.5 rounded-2xl bg-gray-50 p-3 ring-1 ring-gray-200">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold uppercase tracking-[0.08em] text-gray-500">
              {t("issues.newCategory")}
            </span>
            <button
              type="button"
              onClick={() => setAdding(false)}
              aria-label={t("common.cancel")}
              className="grid h-6 w-6 place-items-center rounded-full text-gray-400 hover:bg-white hover:text-gray-700"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>

          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              // Enter inside a form submits the form; this field is not the
              // form's business, so it handles its own key and stops there.
              if (e.key === "Enter") {
                e.preventDefault();
                submitNew();
              }
            }}
            maxLength={60}
            placeholder={t("issues.newCategoryPlaceholder")}
            className="h-10 w-full rounded-xl bg-white px-3 text-sm ring-1 ring-gray-200 focus:outline-none focus:ring-2 focus:ring-[#1F3A93]"
          />

          <div className="flex flex-wrap gap-1.5">
            {CATEGORY_ICON_CHOICES.map(({ name: iconName, Icon }) => (
              <button
                key={iconName}
                type="button"
                onClick={() => setIcon(iconName)}
                aria-label={iconName}
                aria-pressed={icon === iconName}
                className={`grid h-9 w-9 place-items-center rounded-xl transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1F3A93] ${
                  icon === iconName
                    ? "bg-[#E7EBF8] text-[#1B3480]"
                    : "bg-white text-gray-500 ring-1 ring-gray-200 hover:bg-gray-100"
                }`}
              >
                <Icon className="h-4 w-4" aria-hidden />
              </button>
            ))}
          </div>

          <button
            type="button"
            onClick={submitNew}
            disabled={name.trim().length < 2 || creating}
            className="h-10 w-full rounded-xl text-sm font-semibold text-white transition disabled:opacity-40"
            style={{ background: BRAND }}
          >
            {creating ? t("common.saving") : t("issues.addCategory")}
          </button>
        </div>
      )}
    </div>
  );
}

type IssueCategoryRow = {
  id: string;
  name: string;
  icon: string;
};

/* ------------------------------------------------------------------ *
 * Triage panel
 *
 * The note field comes BEFORE the status buttons, and that ordering is the
 * whole reason this is a component.
 *
 * It used to sit below them. A status button applies immediately, so anyone
 * reading top to bottom clicked the status and only then saw the note field
 * — by which time the change was already saved without it. The note was
 * effectively unreachable. Reading order has to match the order things
 * happen in.
 *
 * Status is shown as a lifecycle rather than four equal buttons: Pending →
 * In Progress → Resolved is a progression, and the current step and the
 * likely next one should be obvious without reading every label. Cancel is
 * deliberately outside that run — it ends an issue rather than advancing it.
 * ------------------------------------------------------------------ */
const LIFECYCLE: IssueStatus[] = ["PENDING", "IN_PROGRESS", "RESOLVED"];

export function TriagePanel({
  status,
  note,
  onNote,
  onStatus,
  busy,
  labels,
  children,
}: {
  status: IssueStatus;
  note: string;
  onNote: (value: string) => void;
  onStatus: (next: IssueStatus) => void;
  busy?: boolean;
  labels: IssueLabels;
  children?: React.ReactNode;
}) {
  const { t } = useLanguage();
  const currentStep = LIFECYCLE.indexOf(status);

  return (
    <section className="rounded-2xl bg-gray-50/80 p-3.5 ring-1 ring-gray-200/70">
      <div className="flex items-center justify-between gap-2">
        <SectionHeading>{t("hoa.issues.triage")}</SectionHeading>
        {busy && (
          <span className="flex items-center gap-1.5 text-[11px] font-medium text-gray-500">
            <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
            {t("common.saving")}
          </span>
        )}
      </div>

      {/* 1. the note — first, because the buttons below consume it */}
      <label className="mt-3 block">
        <span className="text-[11px] font-medium text-gray-600">
          {t("hoa.issues.statusNote")}
        </span>
        <input
          value={note}
          onChange={(e) => onNote(e.target.value)}
          placeholder={t("hoa.issues.statusNotePlaceholder")}
          className="mt-1 h-10 w-full rounded-xl bg-white px-3 text-sm ring-1 ring-gray-200 transition placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#1F3A93]"
        />
      </label>

      {/* 2. the lifecycle */}
      <div className="mt-3 flex items-stretch gap-1.5">
        {LIFECYCLE.map((s, i) => {
          const tone = STATUS_TONE[s];
          const isCurrent = status === s;
          const isPast = currentStep > i && currentStep !== -1;
          const Icon = STATUS_ICON[s];
          return (
            <button
              key={s}
              type="button"
              onClick={() => onStatus(s)}
              disabled={busy || isCurrent}
              aria-current={isCurrent ? "step" : undefined}
              className={`flex flex-1 flex-col items-center justify-center gap-1 rounded-xl px-1 py-2.5 text-center transition disabled:cursor-default focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1F3A93] ${
                isCurrent || isPast
                  ? ""
                  : "bg-white text-gray-500 ring-1 ring-gray-200 hover:bg-gray-100 hover:text-gray-800"
              }`}
              style={
                isCurrent
                  ? { background: tone.bg, color: tone.fg, boxShadow: `inset 0 0 0 1.5px ${tone.rail}` }
                  : isPast
                  ? { background: tone.bg, color: tone.fg, opacity: 0.55 }
                  : undefined
              }
            >
              <Icon className="h-4 w-4" aria-hidden />
              <span className="text-[11px] font-semibold leading-tight">
                {labels.status[s]}
              </span>
            </button>
          );
        })}
      </div>

      {/* 3. cancel — outside the run, because it ends rather than advances */}
      <button
        type="button"
        onClick={() => onStatus("CANCELLED")}
        disabled={busy || status === "CANCELLED"}
        className={`mt-1.5 w-full rounded-xl px-3 py-2 text-[12px] font-medium transition disabled:cursor-default focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1F3A93] ${
          status === "CANCELLED"
            ? ""
            : "text-gray-500 ring-1 ring-gray-200 hover:bg-white hover:text-gray-800"
        }`}
        style={
          status === "CANCELLED"
            ? {
                background: STATUS_TONE.CANCELLED.bg,
                color: STATUS_TONE.CANCELLED.fg,
                boxShadow: `inset 0 0 0 1.5px ${STATUS_TONE.CANCELLED.rail}`,
              }
            : undefined
        }
      >
        {status === "CANCELLED"
          ? labels.status.CANCELLED
          : t("hoa.issues.actions.cancel")}
      </button>

      {children}
    </section>
  );
}

/* ------------------------------------------------------------------ *
 * Comments
 * ------------------------------------------------------------------ */
export function CommentThread({
  comments,
  value,
  onValue,
  onSubmit,
  busy,
  canMarkInternal,
  isInternal,
  onIsInternal,
  error,
}: {
  comments: { id: string; body: string; is_internal: boolean; author_name: string | null; created_at: string }[];
  value: string;
  onValue: (v: string) => void;
  onSubmit: () => void;
  busy?: boolean;
  canMarkInternal?: boolean;
  isInternal?: boolean;
  onIsInternal?: (v: boolean) => void;
  error?: unknown;
}) {
  const { t } = useLanguage();

  return (
    <section>
      <div className="flex items-center gap-2">
        <SectionHeading>{t("issues.comments")}</SectionHeading>
        {comments.length > 0 && (
          <span className="rounded-full bg-gray-100 px-1.5 text-[10px] font-bold tabular-nums text-gray-600">
            {comments.length}
          </span>
        )}
      </div>

      <div className="mt-3 space-y-2">
        {comments.length === 0 && (
          <p className="rounded-xl bg-gray-50 px-3 py-2.5 text-sm text-gray-500">
            {t("issues.noComments")}
          </p>
        )}
        {comments.map((c) => (
          <article
            key={c.id}
            className={`rounded-2xl p-3 text-sm ring-1 ${
              c.is_internal
                ? "bg-amber-50 text-amber-950 ring-amber-200"
                : "bg-white text-gray-800 ring-gray-200"
            }`}
          >
            <header className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-gray-500">
              <span className="font-semibold text-gray-800">{c.author_name ?? "—"}</span>
              {c.is_internal && (
                <span className="rounded-full bg-amber-200/70 px-1.5 py-px text-[10px] font-bold uppercase tracking-wide text-amber-900">
                  {t("issues.internalNote")}
                </span>
              )}
              <span className="ml-auto tabular-nums">
                {new Date(c.created_at).toLocaleString()}
              </span>
            </header>
            <p className="mt-1.5 whitespace-pre-line leading-relaxed">{c.body}</p>
          </article>
        ))}
      </div>

      <div className="mt-3 rounded-2xl bg-white p-2.5 ring-1 ring-gray-200 focus-within:ring-2 focus-within:ring-[#1F3A93]">
        <textarea
          value={value}
          onChange={(e) => onValue(e.target.value)}
          rows={2}
          placeholder={t("issues.commentPlaceholder")}
          className="w-full resize-y bg-transparent text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none"
        />
        <div className="mt-1.5 flex items-center justify-between gap-2 border-t border-gray-100 pt-2">
          {canMarkInternal ? (
            <label className="flex cursor-pointer select-none items-center gap-2 text-[11px] font-medium text-gray-600">
              <input
                type="checkbox"
                checked={!!isInternal}
                onChange={(e) => onIsInternal?.(e.target.checked)}
                className="h-3.5 w-3.5 accent-amber-600"
              />
              {t("issues.internalOnly")}
            </label>
          ) : (
            <span />
          )}
          <button
            type="button"
            disabled={!value.trim() || busy}
            onClick={onSubmit}
            className="inline-flex items-center gap-1.5 rounded-xl px-3.5 py-1.5 text-xs font-semibold text-white transition disabled:opacity-40"
            style={{ background: BRAND }}
          >
            {busy ? (
              <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
            ) : (
              <Send className="h-3 w-3" aria-hidden />
            )}
            {busy ? t("common.saving") : t("issues.addComment")}
          </button>
        </div>
      </div>
      <ApiError error={error} />
    </section>
  );
}



/* ------------------------------------------------------------------ *
 * Attachments
 *
 * Both create forms accepted files, and both grew their own copy of the
 * limits, the validation and the chip list. Two copies of a rule that must
 * mirror issues/serializers.py is one copy too many: the day the server
 * accepts WEBP and one of them still rejects it, nobody will think to look
 * in the other file.
 * ------------------------------------------------------------------ */

/**
 * The upload well.
 *
 * Image thumbnails rather than a paperclip per row: the whole point of a
 * photo attached to a maintenance report is that you can see it, and the
 * admin's copy of this showed five identical grey rows for five photos.
 */
export function FileDrop({
  files,
  error,
  onAdd,
  onRemove,
}: {
  files: File[];
  error: string;
  onAdd: (list: FileList | null) => void;
  onRemove: (index: number) => void;
}) {
  const { t } = useLanguage();

  return (
    <div>
      <label className="flex cursor-pointer flex-col items-center justify-center gap-1.5 rounded-xl border border-dashed border-gray-300 bg-white px-4 py-5 text-center transition hover:border-[#1F3A93]/50 hover:bg-[#1F3A93]/[0.02]">
        <input
          type="file"
          multiple
          accept="image/png,image/jpeg,image/webp,application/pdf"
          className="hidden"
          onChange={(e) => {
            onAdd(e.target.files);
            // Cleared so re-picking the same file fires onChange again.
            e.target.value = "";
          }}
        />
        <span className="grid h-9 w-9 place-items-center rounded-full bg-[#E7EBF8] text-[#1B3480]">
          <Paperclip className="h-4 w-4" aria-hidden />
        </span>
        <span className="text-sm font-medium text-gray-800">{t("issues.clickToUpload")}</span>
        <span className="text-[11px] text-gray-500">{t("issues.attachmentHint")}</span>
      </label>

      {files.length > 0 && (
        <ul className="mt-2 space-y-1.5">
          {files.map((file, i) => (
            <FileRow key={`${file.name}-${i}`} file={file} onRemove={() => onRemove(i)} />
          ))}
        </ul>
      )}

      {error && <p className="mt-1.5 text-xs font-medium text-red-600">{error}</p>}
    </div>
  );
}

function FileRow({ file, onRemove }: { file: File; onRemove: () => void }) {
  const { t } = useLanguage();
  const isImage = file.type.startsWith("image/");

  // Revoked on unmount. Without this every preview holds its blob for the
  // life of the tab, and a resident who adds and removes photos a few times
  // leaks every one of them.
  const [preview, setPreview] = React.useState<string>("");
  React.useEffect(() => {
    if (!isImage) return;
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file, isImage]);

  return (
    <li className="flex items-center gap-2 rounded-lg bg-white px-2.5 py-2 text-sm ring-1 ring-gray-200">
      {isImage && preview ? (
        <img src={preview} alt="" className="h-8 w-8 shrink-0 rounded object-cover" />
      ) : (
        <span className="grid h-8 w-8 shrink-0 place-items-center rounded bg-gray-100 text-[9px] font-bold text-gray-500">
          PDF
        </span>
      )}
      <span className="min-w-0 flex-1 truncate text-gray-800">{file.name}</span>
      <span className="shrink-0 text-[11px] tabular-nums text-gray-400">
        {formatBytes(file.size)}
      </span>
      <button
        type="button"
        onClick={onRemove}
        aria-label={t("common.delete")}
        className="grid h-7 w-7 shrink-0 place-items-center rounded-full text-gray-400 transition hover:bg-gray-100 hover:text-red-600"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </li>
  );
}


/* ------------------------------------------------------------------ *
 * Who is this about
 * ------------------------------------------------------------------ */

/**
 * Common area is a CHOICE, not the absence of one.
 *
 * The old form expressed it as the empty option of a unit dropdown, which
 * made "this affects the whole building" and "I forgot to pick a unit" the
 * same input — and the second one filed a building-wide issue with a 201.
 */
export type TargetScope = "COMMON" | "UNIT";

export function TargetPicker({
  units,
  scope,
  unitId,
  onChange,
  loading,
}: {
  units: IssueTargetUnit[];
  scope: TargetScope;
  unitId: string | null;
  onChange: (scope: TargetScope, unitId: string | null) => void;
  loading?: boolean;
}) {
  const { t } = useLanguage();
  const [query, setQuery] = React.useState("");

  // Matches the unit code OR anyone who lives there. An admin looking for a
  // leak in Maria's apartment knows "Maria", not "B-204".
  const matches = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return units;
    return units.filter(
      (u) =>
        u.unit_code.toLowerCase().includes(q) ||
        u.residents.some(
          (r) =>
            r.name.toLowerCase().includes(q) || r.email.toLowerCase().includes(q)
        )
    );
  }, [units, query]);

  const selected = units.find((u) => u.id === unitId) ?? null;

  return (
    <div className="space-y-2">
      <div className="flex gap-1 rounded-xl bg-gray-100/80 p-1">
        <ScopeTab
          active={scope === "COMMON"}
          icon={<Building2 className="h-3.5 w-3.5" aria-hidden />}
          label={t("issues.commonArea")}
          onClick={() => onChange("COMMON", null)}
        />
        <ScopeTab
          active={scope === "UNIT"}
          icon={<Home className="h-3.5 w-3.5" aria-hidden />}
          label={t("issues.aSpecificUnit")}
          onClick={() => onChange("UNIT", unitId)}
        />
      </div>

      {scope === "COMMON" ? (
        <p className="rounded-xl bg-[#E7EBF8]/60 px-3 py-2.5 text-[12px] leading-relaxed text-[#1B3480]">
          {t("issues.commonAreaExplainer")}
        </p>
      ) : loading ? (
        <div className="space-y-1.5" aria-hidden>
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-12 animate-pulse rounded-xl bg-gray-100" />
          ))}
        </div>
      ) : (
        <>
          <div className="relative">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
              aria-hidden
            />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("issues.searchUnitOrResident")}
              aria-label={t("issues.searchUnitOrResident")}
              className="h-11 w-full rounded-xl bg-white pl-9 pr-3 text-[15px] text-gray-900 ring-1 ring-gray-200 transition placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#1F3A93]"
            />
          </div>

          {matches.length === 0 ? (
            <p className="rounded-xl bg-gray-50 px-3 py-4 text-center text-[13px] text-gray-500">
              {units.length === 0
                ? t("issues.noUnitsInBuilding")
                : t("issues.noUnitMatches")}
            </p>
          ) : (
            /* Scrolls rather than growing: a 60-unit building would push the
               modal's save button off a phone screen entirely. */
            <ul
              role="listbox"
              aria-label={t("issues.searchUnitOrResident")}
              className="max-h-60 space-y-1 overflow-y-auto rounded-xl bg-gray-50 p-1 ring-1 ring-gray-200"
            >
              {matches.map((u) => {
                const active = u.id === unitId;
                return (
                  <li key={u.id}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={active}
                      onClick={() => onChange("UNIT", u.id)}
                      className={`flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1F3A93] ${
                        active ? "bg-white shadow-sm" : "hover:bg-white/70"
                      }`}
                      style={active ? { boxShadow: `inset 0 0 0 1.5px ${BRAND}` } : undefined}
                    >
                      <span
                        className={`grid h-8 w-8 shrink-0 place-items-center rounded-lg text-[11px] font-bold ${
                          active ? "bg-[#E7EBF8] text-[#1B3480]" : "bg-white text-gray-500 ring-1 ring-gray-200"
                        }`}
                      >
                        {u.unit_code.split("-").pop()}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[13px] font-semibold text-gray-900">
                          {u.unit_code}
                        </span>
                        <span className="block truncate text-[11px] text-gray-500">
                          {u.residents.length
                            ? u.residents.map((r) => r.name).join(", ")
                            : t("issues.noResidents")}
                        </span>
                      </span>
                      {active && <Check className="h-4 w-4 shrink-0 text-[#1B3480]" aria-hidden />}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          {/* Named explicitly once chosen. The list scrolls, so the selected
              row is frequently out of view by the time you reach Save. */}
          {selected && (
            <p className="text-[12px] text-gray-600">
              {t("issues.willNotify")}{" "}
              <span className="font-semibold text-gray-900">
                {selected.residents.length
                  ? selected.residents.map((r) => r.name).join(", ")
                  : t("issues.noResidents")}
              </span>
            </p>
          )}
        </>
      )}
    </div>
  );
}

function ScopeTab({
  active,
  icon,
  label,
  onClick,
}: {
  active: boolean;
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`flex flex-1 items-center justify-center gap-1.5 rounded-lg py-2 text-[12px] font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1F3A93] ${
        active ? "bg-white text-gray-900 shadow-sm" : "text-gray-600 hover:text-gray-900"
      }`}
    >
      {icon}
      {label}
    </button>
  );
}
